import React from "react";
import { Check, ChevronRight } from "lucide-react";
import { getBenefitCardConfig, BenefitIcon } from "../utils/benefitIconMap";

export function getFulfillmentDisplay(fulfillment, serviceTypeName) {
  const rules = fulfillment?.rules;

  if (!Array.isArray(rules) || rules.length === 0) {
    return [];
  }

  const serviceName = serviceTypeName?.trim() || "this service";

  const displays = [];

  rules.forEach((rule) => {
    if (typeof rule?.wallet === "number") {
      displays.push({
        type: "wallet",
        value: rule.wallet,
        label: `₹${rule.wallet.toLocaleString("en-IN")} Wallet for ${serviceName}`,
      });
    }

    if (typeof rule?.credit === "number") {
      displays.push({
        type: "credit",
        value: rule.credit,
        label:
          rule.credit === 0
            ? `Unlimited Consultation for ${serviceName}`
            : `${rule.credit} Credit${
                rule.credit === 1 ? "" : "s"
              } for ${serviceName}`,
      });
    }
    if (rule?.copay && typeof rule.copay === "object") {
      const value = Number(rule.copay.value ?? 0);
      const percentage = rule.copay.percentage;

      if (percentage === true) {
        if (value === 0) {
          displays.push({
            type: "copay",
            value,
            label: `Unlimited for ${serviceName}`,
          });
        } else if (value === 100) {
          displays.push({
            type: "copay",
            value,
            label: `Pay per use`,
          });
        } else {
          displays.push({
            type: "copay",
            value,
            label: `Pay ${value}% for ${serviceName}`,
          });
        }
      } else {
        if (value === 0) {
          displays.push({
            type: "copay",
            value,
            label: `Unlimited for ${serviceName}`,
          });
        } else {
          displays.push({
            type: "copay",
            value,
            label: `Pay ₹${value.toLocaleString("en-IN")} for ${serviceName}`,
          });
        }
      }
    }
  });

  return displays;
}

const BenefitCard = ({ benefit, selected, onToggleSelect, onViewDetails }) => {
  if (!benefit || typeof benefit !== "object") {
    return (
      <div className="rounded-2xl border border-dashed border-[#E8E5F0] bg-[#FAFAFC] p-5 text-center">
        <p className="text-sm text-[#8A8798]">Benefit data unavailable</p>
      </div>
    );
  }

  const hasFulfillments = Array.isArray(benefit.fulfillments) && benefit.fulfillments.length > 0;
  const firstFulfillment = hasFulfillments ? benefit.fulfillments[0] : null;
  const firstSku = firstFulfillment?.skus?.[0];

  const serviceTypeName = firstSku?.serviceName;
  const benefitName = serviceTypeName || benefit?.name || "Untitled Benefit";

  // `icon` can be a path to a custom asset (string) or a lucide-react
  // component, depending on the benefit — BenefitIcon handles both.
  const config = getBenefitCardConfig(serviceTypeName);
  const { tint, tagline, highlights } = config;

  const fulfillmentDisplay = firstFulfillment
    ? getFulfillmentDisplay(firstFulfillment, serviceTypeName)
    : [];

  const amount = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(benefit?.benefitAmount || 0);

  const handleCardClick = () => onToggleSelect?.(benefit);
  const handleViewDetails = (e) => {
    e.stopPropagation();
    onViewDetails?.(benefit);
  };

  return (
    <div
      onClick={handleCardClick}
      role="checkbox"
      aria-checked={selected}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardClick();
        }
      }}
      className={`group relative flex flex-col w-full text-left rounded-2xl border bg-white p-4 cursor-pointer transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-[#6D28D9] focus-visible:ring-offset-2 hover:-translate-y-1 ${
        selected
          ? "border-[#6D28D9] shadow-[0_6px_20px_rgba(109,40,217,0.14)]"
          : "border-[#E8E5F0] shadow-[0_1px_2px_rgba(20,19,26,0.03)] hover:border-[#C9BEE8] hover:shadow-[0_12px_28px_rgba(20,19,26,0.08)]"
      }`}
    >
      {/* Selection toggle */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          handleCardClick();
        }}
        aria-label={selected ? `Remove ${benefitName}` : `Add ${benefitName}`}
        className={`absolute top-3.5 right-3.5 h-5 w-5 rounded-full flex items-center justify-center border transition-colors duration-150 ${
          selected
            ? "bg-[#6D28D9] border-[#6D28D9] text-white"
            : "bg-white border-[#D8D5E0] text-[#6D28D9] group-hover:border-[#6D28D9]"
        }`}
      >
        {selected && <Check size={12} strokeWidth={2.5} />}
      </button>

      {/* Icon + Service name + Tagline */}
      <div className="flex items-start gap-3 pr-7">
        <BenefitIcon config={config} size={44} />
        <div className="min-w-0 pt-0.5">
          <p className="text-[15px] font-bold text-[#14131A] leading-snug truncate">
            {benefitName}
          </p>
          <p className="mt-0.5 text-[11.5px] text-[#8A8798] leading-snug line-clamp-2">
            {tagline}
          </p>
        </div>
      </div>

      {/* Wallet / Credits / Co-pay badges */}
      <div className="hide-scrollbar mt-3 flex max-w-full flex-nowrap gap-1.5 overflow-x-auto">
        {fulfillmentDisplay.map((item, index) => (
          <span
            key={`${item.type}-${index}`}
            className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2.5 py-1 text-[10px] font-bold"
            style={{
              backgroundColor: tint.bg,
              color: tint.fg,
              borderColor: `${tint.fg}30`,
            }}
          >
            {item.label}
          </span>
        ))}
      </div>

      {/* Service highlights */}
      {highlights?.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {highlights.slice(0, 3).map((point) => (
            <li
              key={point}
              className="flex items-start gap-1.5 text-[11.5px] text-[#5B5866]"
            >
              <span
                className="mt-[5px] h-1 w-1 rounded-full shrink-0"
                style={{ backgroundColor: tint.fg }}
              />
              <span className="leading-snug">{point}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Footer: Give Details + Benefit Amount */}
      <div className="mt-4 pt-3 border-t border-[#F0EEF5] flex items-center justify-between">
        {hasFulfillments ? (
          <button
            type="button"
            onClick={handleViewDetails}
            className="inline-flex items-center border px-2 py-1 rounded-sm bg-purple-100 border-[#6D28D9] gap-0.5 text-[11px] font-semibold text-[#6D28D9] hover:gap-1 transition-all duration-150"
            aria-label={`View details for ${benefitName}`}
          >
            Give Details
            <ChevronRight size={11} strokeWidth={2.5} />
          </button>
        ) : (
          <span />
        )}
        <div className="text-right">
          <p className="text-[11px] font-medium text-[#64626c] leading-none">
            Benefit Amount
          </p>
          <p className="text-[13px] font-bold text-[#14131A] ">{amount}</p>
        </div>
      </div>
    </div>
  );
};

export default BenefitCard;